import getVideoId from "get-video-id";
import VideoGate from "./VideoGate";

// Re-exported from a cycle-free module: `VideoGate` also needs these, and
// importing them straight from here would create a `Video ↔ VideoGate` import
// cycle. Other consumers (e.g. the PeerTube `VideoPlayer`) keep importing from
// `Video.jsx`.
export {
  ResponsiveIFrame,
  responsiveIFrameContainer,
} from "./ResponsiveIFrame.jsx";

// Returns a gate descriptor for a German mediathek (ARD/ZDF/arte) embed, or null.
// `id` (and `lang` for arte) is the opaque provider id the poster endpoint needs;
// `embedSrc` is the iframe URL loaded on click, `autoplayParam` is appended then.
const matchMediathekEmbed = (url) => {
  let m;
  m = url.match(/ardmediathek\.de\/video\/[^/]+\/[^/]+\/[^/]+\/([^/?#]+)/);
  if (m)
    return {
      service: "ardmediathek",
      id: m[1],
      embedSrc: `https://www.ardmediathek.de/embed/${m[1]}`,
      allow: "autoplay; fullscreen; clipboard-read; clipboard-write",
      // ARD registers an `autoplay` embed param but doesn't reliably honor it
      // from the URL — best-effort, may need one extra click inside the player.
      autoplayParam: "autoplay=true",
    };
  m = url.match(/zdf\.de\/play\/[^/]+\/[^/]+\/([^/?#]+)/);
  if (m)
    return {
      service: "zdf",
      id: m[1],
      embedSrc: `https://ngp.zdf.de/miniplayer/embed/?mediaID=${m[1]}`,
      allow: "autoplay; fullscreen",
      // ZDF's ngp miniplayer reads the camelCase `autoPlay` query param.
      autoplayParam: "autoPlay=true",
    };
  m = url.match(/arte\.tv\/([a-z]{2})\/videos\/([^/?#]+)/);
  if (m)
    return {
      service: "arte",
      id: m[2],
      lang: m[1],
      embedSrc: `https://www.arte.tv/embeds/${m[1]}/${m[2]}`,
      allow: "autoplay; fullscreen",
      autoplayParam: "autoplay=1",
    };
  return null;
};

/**
 * VideoEmbed is a React functional component used to render an embedded video player for various video services.
 * It takes a video URL as input and determines the appropriate video platform to generate an embeddable iframe.
 * The component supports the popular video services Facebook, Vimeo, and YouTube.
 *
 * @param {object} props - Component properties.
 * @param {string} props.url - The URL of the video to be embedded. This should be a fully-qualified URL for the desired video.
 * @returns {JSX.Element} - A React element containing the responsive iframe for the video or a message indicating an error or unsupported service.
 */
const VideoEmbed = ({ url }) => {
  if (!url) return <div>No video url given</div>;

  const getWidthHeight = () => {
    const ratioMatch = url.match(/#ratio:(\d+)x(\d+)/);
    if (ratioMatch)
      return [parseInt(ratioMatch[1], 10), parseInt(ratioMatch[2], 10)];
    const widthMatch = url.match(/width="?(\d+)/i);
    const heightMatch = url.match(/height="?(\d+)/i);
    if (widthMatch && heightMatch) {
      return [parseInt(widthMatch[1]), parseInt(heightMatch[1])];
    }
    return [16, 9];
  };

  const [w, h] = getWidthHeight();
  const ratio = h / w;

  // Every external embed funnels through the same click-to-play gate, so no
  // third-party request fires until the user explicitly clicks (privacy/GDPR).
  const mediathek = matchMediathekEmbed(url);
  if (mediathek) {
    return <VideoGate ratio={ratio} {...mediathek} />;
  }

  const { id, service } = getVideoId(url);

  if (!id) return <div>Unkown Video ID</div>;

  if (service === "vimeo") {
    return (
      <VideoGate
        service="vimeo"
        id={id}
        ratio={ratio}
        embedSrc={`https://player.vimeo.com/video/${id}?dnt=1`}
        allow="autoplay; fullscreen"
        autoplayParam="autoplay=1"
      />
    );
  }

  if (service === "youtube") {
    return (
      <VideoGate
        service="youtube"
        id={id}
        ratio={ratio}
        embedSrc={`https://www.youtube-nocookie.com/embed/${id}?rel=0`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        autoplayParam="autoplay=1"
      />
    );
  }
  return <div>Unkown source</div>;
};

export default VideoEmbed;
